import React from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import type {
  FieldCondition,
  FieldConditionOperator,
  FieldConditionType,
} from '../../types/ticketTypeTemplate';
import { HelpHint } from '../admin/HelpHint';
import { FIELD_SX } from '../../styles/designSystem';

// Hover-help text shown via HelpHint next to each field's label.
// Lives next to the component because the wording is tightly coupled
// to how `evaluateCondition` reads each part.
const HELP = {
  source:
    'Where this condition reads its value from. ' +
    '"Field value" compares against another field on this form. ' +
    '"Customer context" compares against attributes of the linked ' +
    'customer (e.g. designation, business name). ' +
    '"Custom (code)" runs a named evaluator — edit via JSON mode.',
  fieldKey:
    'Identifier of the source field. For "Field value" this is the ' +
    'key as defined on the form (e.g. device_type, service_mode). ' +
    'For "Customer context" use one of the customer attribute keys ' +
    '(e.g. designation, businessName).',
  operator:
    'How the source value is compared to the value below. ' +
    '"equals" / "not equals" — exact match. ' +
    '"greater than" / "less than" — numeric comparison. ' +
    '"contains" — substring match on strings. ' +
    '"is in list" — matches if the source equals any value in the list. ' +
    '"custom" — invokes a named evaluator (value field disabled).',
  value:
    'Right-hand value the source is compared to. Match the casing of ' +
    'the field\'s stored value (e.g. "laptop" not "Laptop"). For "is in ' +
    'list" press Enter after each option. Ignored when operator is "custom".',
  taskSource:
    'The other task in this list whose result this item depends on. ' +
    'The item stays hidden until that task is actioned.',
  taskAspect:
    '"Selected outcome" reads the source task\'s chosen dropdown option; ' +
    '"Is complete" reads whether the source task is marked done.',
  taskOutcomeValue:
    'The outcome value(s) that reveal this item. If the source task uses a ' +
    'dropdown set, pick from its options; otherwise type the value. For "is ' +
    'in list" press Enter after each option.',
  addCondition:
    'Show this only when a condition matches. By default it is always ' +
    'shown — leave this off unless you need conditional visibility.',
  clearInherited:
    'Override an inherited condition by saving a null on this row, so ' +
    'the inherited rule no longer applies here.',
  restoreInherited:
    'Drop this override and fall back to the inherited condition.',
  removeCondition:
    'Drop this condition. The item will then always be shown.',
} as const;

export type AnyCondition = FieldCondition;

type ConditionTypeOption = FieldConditionType;
type ConditionOperatorOption = FieldConditionOperator;

/** A task in the same list, offered as a `taskOutcome` condition source. */
export interface ConditionTaskOption {
  taskId: string;
  title: string;
  /** Resolved dropdown option labels, when the task is a dropdown task —
   *  used to populate the outcome value picker. */
  dropdownOptions?: string[];
}

interface ConditionEditorProps {
  value: AnyCondition | null | undefined;
  onChange: (next: AnyCondition | null | undefined) => void;
  allowedFieldKeys?: string[];
  allowCustom?: boolean;
  /**
   * When true, exposes a "Task outcome" source — condition a checklist item
   * on another task's selected outcome or completion. `taskOptions` supplies
   * the pickable source tasks (siblings in the same task list).
   */
  allowTaskOutcome?: boolean;
  taskOptions?: ConditionTaskOption[];
  /** When true, exposes a "clear inherited" toggle that emits `null` instead of `undefined`. */
  allowNull?: boolean;
  /** Heading label above the editor. */
  label?: string;
}

const TYPE_LABELS: Record<ConditionTypeOption, string> = {
  fieldValue: 'Field value',
  customerContext: 'Customer context',
  custom: 'Custom (code)',
  taskOutcome: 'Task outcome',
};

const OPERATOR_LABELS: Record<ConditionOperatorOption, string> = {
  equals: 'equals',
  notEquals: 'not equals',
  greaterThan: 'greater than',
  lessThan: 'less than',
  contains: 'contains',
  in: 'is in list',
  custom: 'custom',
};

function coerceListEntry(raw: string): string | number | boolean {
  const trimmed = raw.trim();
  if (trimmed === 'true') return true;
  if (trimmed === 'false') return false;
  if (trimmed !== '' && !Number.isNaN(Number(trimmed)) && String(Number(trimmed)) === trimmed) {
    return Number(trimmed);
  }
  return raw;
}

function defaultCondition(): AnyCondition {
  return { type: 'fieldValue', fieldKey: '', operator: 'equals', value: '' };
}

export const ConditionEditor: React.FC<ConditionEditorProps> = ({
  value,
  onChange,
  allowedFieldKeys,
  allowCustom = false,
  allowTaskOutcome = false,
  taskOptions,
  allowNull = false,
  label = 'Show When',
}) => {
  const isCleared = allowNull && value === null;
  const hasCondition = !isCleared && value != null && value !== undefined;

  const handleAdd = () => onChange(defaultCondition());
  const handleClear = () => onChange(allowNull ? null : undefined);
  const handleRemove = () => onChange(undefined);

  if (!hasCondition) {
    return (
      <Box sx={{ border: '0.5px dashed #d0dcea', borderRadius: '8px', p: 1.5, bgcolor: '#fafbfd' }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
            {label}: {isCleared ? <em>cleared (overrides inherited)</em> : <em>always shown</em>}
          </Typography>
          {!isCleared && (
            <>
              <Button size="small" onClick={handleAdd}>
                Add condition
              </Button>
              <HelpHint text={HELP.addCondition} />
            </>
          )}
          {allowNull && !isCleared && (
            <>
              <Button size="small" onClick={handleClear} color="warning">
                Clear inherited
              </Button>
              <HelpHint text={HELP.clearInherited} />
            </>
          )}
          {isCleared && (
            <>
              <Button size="small" onClick={handleRemove}>
                Restore inherited
              </Button>
              <HelpHint text={HELP.restoreInherited} />
            </>
          )}
        </Stack>
      </Box>
    );
  }

  const cond = value as AnyCondition;
  const isCustom = cond.type === 'custom';
  const isTaskOutcome = cond.type === 'taskOutcome';
  const isCustomOperator = cond.operator === 'custom';

  const setField = (patch: Partial<AnyCondition>) => {
    onChange({ ...cond, ...patch });
  };

  const handleSourceChange = (nextType: ConditionTypeOption) => {
    if (nextType === cond.type) return;
    // Switching into or out of taskOutcome swaps the entire value shape
    // (taskId/aspect vs fieldKey), so reset to that type's defaults.
    if (nextType === 'taskOutcome') {
      onChange({ type: 'taskOutcome', taskId: '', aspect: 'outcome', operator: 'equals', value: '' });
    } else if (isTaskOutcome) {
      onChange({ type: nextType, fieldKey: '', operator: 'equals', value: '' });
    } else {
      setField({ type: nextType });
    }
  };

  const taskAspect = cond.aspect ?? 'outcome';
  const selectedTaskOptions =
    taskOptions?.find((t) => t.taskId === cond.taskId)?.dropdownOptions ?? [];

  return (
    <Box sx={{ border: '0.5px solid #e2eaf3', borderRadius: '8px', p: 1.5, bgcolor: '#fafbfd' }}>
      <Stack spacing={1.5}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="subtitle2" sx={{ flex: 1 }}>
            {label}
          </Typography>
          <Button size="small" onClick={handleRemove}>
            Remove condition
          </Button>
          <HelpHint text={HELP.removeCondition} />
        </Stack>

        {isCustom ? (
          <Chip
            label="Custom evaluator — edit via JSON mode"
            color="warning"
            variant="outlined"
          />
        ) : (
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="flex-start">
            <Stack direction="row" alignItems="center" sx={{ minWidth: 160 }}>
              <FormControl size="small" sx={[{ flex: 1 }, FIELD_SX]}>
                <InputLabel>Source</InputLabel>
                <Select
                  label="Source"
                  value={cond.type}
                  onChange={(e) => handleSourceChange(e.target.value as ConditionTypeOption)}
                >
                  <MenuItem value="fieldValue">{TYPE_LABELS.fieldValue}</MenuItem>
                  <MenuItem value="customerContext">{TYPE_LABELS.customerContext}</MenuItem>
                  {allowTaskOutcome && <MenuItem value="taskOutcome">{TYPE_LABELS.taskOutcome}</MenuItem>}
                  {allowCustom && <MenuItem value="custom">{TYPE_LABELS.custom}</MenuItem>}
                </Select>
              </FormControl>
              <HelpHint text={HELP.source} />
            </Stack>

            {isTaskOutcome ? (
              <>
                <Stack direction="row" alignItems="center" sx={{ flex: 1, minWidth: 160 }}>
                  <FormControl size="small" sx={[{ flex: 1 }, FIELD_SX]}>
                    <InputLabel>Task</InputLabel>
                    <Select
                      label="Task"
                      value={cond.taskId ?? ''}
                      onChange={(e) => setField({ taskId: e.target.value })}
                    >
                      {(taskOptions ?? []).map((t) => (
                        <MenuItem key={t.taskId} value={t.taskId}>
                          {t.title}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <HelpHint text={HELP.taskSource} />
                </Stack>

                <Stack direction="row" alignItems="center" sx={{ minWidth: 150 }}>
                  <FormControl size="small" sx={[{ flex: 1 }, FIELD_SX]}>
                    <InputLabel>Reads</InputLabel>
                    <Select
                      label="Reads"
                      value={taskAspect}
                      onChange={(e) => {
                        const next = e.target.value as 'outcome' | 'completed';
                        setField(
                          next === 'completed'
                            ? { aspect: next, operator: 'equals', value: true }
                            : { aspect: next, operator: 'equals', value: '' },
                        );
                      }}
                    >
                      <MenuItem value="outcome">Selected outcome</MenuItem>
                      <MenuItem value="completed">Is complete</MenuItem>
                    </Select>
                  </FormControl>
                  <HelpHint text={HELP.taskAspect} />
                </Stack>

                {taskAspect === 'completed' ? (
                  <Stack direction="row" alignItems="center" sx={{ minWidth: 150 }}>
                    <FormControl size="small" sx={[{ flex: 1 }, FIELD_SX]}>
                      <InputLabel>State</InputLabel>
                      <Select
                        label="State"
                        value={cond.value === false ? 'false' : 'true'}
                        onChange={(e) =>
                          setField({ operator: 'equals', value: e.target.value === 'true' })
                        }
                      >
                        <MenuItem value="true">is complete</MenuItem>
                        <MenuItem value="false">is not complete</MenuItem>
                      </Select>
                    </FormControl>
                  </Stack>
                ) : (
                  <>
                    <Stack direction="row" alignItems="center" sx={{ minWidth: 140 }}>
                      <FormControl size="small" sx={[{ flex: 1 }, FIELD_SX]}>
                        <InputLabel>Operator</InputLabel>
                        <Select
                          label="Operator"
                          value={cond.operator}
                          onChange={(e) => {
                            const next = e.target.value as ConditionOperatorOption;
                            if (next === 'in' && cond.operator !== 'in') {
                              const seed =
                                cond.value != null && cond.value !== '' ? [cond.value] : [];
                              setField({ operator: next, value: seed });
                            } else if (next !== 'in' && cond.operator === 'in') {
                              setField({ operator: next, value: '' });
                            } else {
                              setField({ operator: next });
                            }
                          }}
                        >
                          <MenuItem value="equals">{OPERATOR_LABELS.equals}</MenuItem>
                          <MenuItem value="notEquals">{OPERATOR_LABELS.notEquals}</MenuItem>
                          <MenuItem value="in">{OPERATOR_LABELS.in}</MenuItem>
                        </Select>
                      </FormControl>
                      <HelpHint text={HELP.operator} />
                    </Stack>

                    <Stack direction="row" alignItems="center" sx={{ flex: 1, minWidth: 160 }}>
                      {cond.operator === 'in' ? (
                        <Autocomplete
                          multiple
                          freeSolo
                          size="small"
                          sx={[{ flex: 1 }, FIELD_SX]}
                          options={selectedTaskOptions}
                          value={
                            Array.isArray(cond.value)
                              ? cond.value.map((v) => String(v))
                              : cond.value != null && cond.value !== ''
                                ? [String(cond.value)]
                                : []
                          }
                          onChange={(_, list) => setField({ value: list.map(coerceListEntry) })}
                          renderTags={(values, getTagProps) =>
                            values.map((v, idx) => (
                              <Chip
                                size="small"
                                variant="outlined"
                                label={v}
                                {...getTagProps({ index: idx })}
                                key={`${v}-${idx}`}
                              />
                            ))
                          }
                          renderInput={(params) => (
                            <TextField {...params} label="Outcome values" placeholder="Press Enter to add" />
                          )}
                        />
                      ) : selectedTaskOptions.length > 0 ? (
                        <Autocomplete
                          freeSolo
                          size="small"
                          sx={[{ flex: 1 }, FIELD_SX]}
                          options={selectedTaskOptions}
                          value={cond.value != null ? String(cond.value) : ''}
                          onChange={(_, v) => setField({ value: v ?? '' })}
                          onInputChange={(_, v) => setField({ value: v })}
                          renderInput={(params) => <TextField {...params} label="Outcome" />}
                        />
                      ) : (
                        <TextField
                          size="small"
                          label="Outcome"
                          value={cond.value ?? ''}
                          onChange={(e) => setField({ value: e.target.value })}
                          sx={[{ flex: 1 }, FIELD_SX]}
                        />
                      )}
                      <HelpHint text={HELP.taskOutcomeValue} />
                    </Stack>
                  </>
                )}
              </>
            ) : (
              <>
            <Stack direction="row" alignItems="center" sx={{ flex: 1, minWidth: 160 }}>
              {allowedFieldKeys && allowedFieldKeys.length > 0 ? (
                <Autocomplete
                  freeSolo
                  size="small"
                  options={allowedFieldKeys}
                  value={cond.fieldKey ?? ''}
                  onChange={(_, v) => setField({ fieldKey: v ?? '' })}
                  onInputChange={(_, v) => setField({ fieldKey: v })}
                  sx={[{ flex: 1 }, FIELD_SX]}
                  renderInput={(params) => <TextField {...params} label="Field key" />}
                />
              ) : (
                <TextField
                  size="small"
                  label="Field key"
                  value={cond.fieldKey ?? ''}
                  onChange={(e) => setField({ fieldKey: e.target.value })}
                  sx={[{ flex: 1 }, FIELD_SX]}
                  slotProps={{ input: { sx: { fontFamily: 'monospace' } } }}
                />
              )}
              <HelpHint text={HELP.fieldKey} />
            </Stack>

            <Stack direction="row" alignItems="center" sx={{ minWidth: 140 }}>
              <FormControl size="small" sx={[{ flex: 1 }, FIELD_SX]}>
                <InputLabel>Operator</InputLabel>
                <Select
                  label="Operator"
                  value={cond.operator}
                  onChange={(e) => {
                    const next = e.target.value as ConditionOperatorOption;
                    const switchingToIn = next === 'in' && cond.operator !== 'in';
                    const switchingFromIn = next !== 'in' && cond.operator === 'in';
                    if (switchingToIn) {
                      const seed = cond.value != null && cond.value !== ''
                        ? [cond.value]
                        : [];
                      setField({ operator: next, value: seed });
                    } else if (switchingFromIn) {
                      setField({ operator: next, value: '' });
                    } else {
                      setField({ operator: next });
                    }
                  }}
                >
                  <MenuItem value="equals">{OPERATOR_LABELS.equals}</MenuItem>
                  <MenuItem value="notEquals">{OPERATOR_LABELS.notEquals}</MenuItem>
                  <MenuItem value="greaterThan">{OPERATOR_LABELS.greaterThan}</MenuItem>
                  <MenuItem value="lessThan">{OPERATOR_LABELS.lessThan}</MenuItem>
                  <MenuItem value="contains">{OPERATOR_LABELS.contains}</MenuItem>
                  <MenuItem value="in">{OPERATOR_LABELS.in}</MenuItem>
                  {allowCustom && <MenuItem value="custom">{OPERATOR_LABELS.custom}</MenuItem>}
                </Select>
              </FormControl>
              <HelpHint text={HELP.operator} />
            </Stack>

            <Stack direction="row" alignItems="center" sx={{ flex: 1, minWidth: 160 }}>
              {cond.operator === 'in' ? (
                <Autocomplete
                  multiple
                  freeSolo
                  size="small"
                  sx={[{ flex: 1 }, FIELD_SX]}
                  options={[] as string[]}
                  value={Array.isArray(cond.value)
                    ? cond.value.map(v => String(v))
                    : cond.value != null && cond.value !== ''
                      ? [String(cond.value)]
                      : []}
                  onChange={(_, list) => setField({ value: list.map(coerceListEntry) })}
                  renderTags={(values, getTagProps) =>
                    values.map((v, idx) => (
                      <Chip
                        size="small"
                        variant="outlined"
                        label={v}
                        {...getTagProps({ index: idx })}
                        key={`${v}-${idx}`}
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField {...params} label="Allowed values" placeholder="Press Enter to add" />
                  )}
                />
              ) : (
                <TextField
                  size="small"
                  label="Value"
                  value={cond.value ?? ''}
                  onChange={(e) => setField({ value: e.target.value })}
                  disabled={isCustomOperator}
                  sx={[{ flex: 1 }, FIELD_SX]}
                />
              )}
              <HelpHint text={HELP.value} />
            </Stack>
              </>
            )}
          </Stack>
        )}

        {allowNull && (
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={false}
                onChange={() => onChange(null)}
              />
            }
            label="Clear inherited condition (saves as null)"
            sx={{ ml: 0 }}
          />
        )}
      </Stack>
    </Box>
  );
};
